import { useQuery } from '@tanstack/react-query';
import { Form, Tag, type FormInstance } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { TabelaPrecoProduto } from '@/features/tabela-preco/types/TabelaPrecoProduto';
import { TableDefault } from '@/components/antd/TableDefault';
import { Container } from '@/components/Container';
import { ErrorComponent } from '@/features/erro/components/ErrorComponent';
import { apiTabelaPrecoProdutoQueryOptions } from '@/features/tabela-preco/api/apiTabelaPrecoProduto';
import { useDialog } from '@/std/dialog/useDialog';
import { numberToCurrency } from '@/std/format';
import { produtoTabTabelaPrecoColumns } from './produtoTabTabelaPrecoColumns';
import { ProdutoTabTabelaPrecoDrawer } from './ProdutoTabTabelaPrecoDrawer';

export function ProdutoTabTabelaPreco({
    produtoIdpk,
    form,
}: {
    produtoIdpk: number;
    form: FormInstance<FormProdutoValues>;
}) {
    const dialog = useDialog<TabelaPrecoProduto>();

    const pro_preco_venda = Form.useWatch('pro_preco_venda', form);
    const pro_preco_minimo = Form.useWatch('pro_preco_minimo', form);

    const { data, isFetching, error } = useQuery(
        apiTabelaPrecoProdutoQueryOptions({ produto_idpk: produtoIdpk }),
    );

    if (error) {
        return <ErrorComponent error={error} />;
    }

    const columns = produtoTabTabelaPrecoColumns(dialog);

    const dataSource = data?.registros
        .toSorted((a, b) => {
            // 1. tap_descricao
            const descCompare = (a.tap_descricao ?? '').localeCompare(
                b.tap_descricao ?? '',
                undefined,
                {
                    sensitivity: 'base',
                },
            );

            if (descCompare !== 0) {
                return descCompare;
            }

            // 2. produtos sem pacote primeiro
            const aHasPacote = Boolean(a.produto_pacote_barra);
            const bHasPacote = Boolean(b.produto_pacote_barra);

            if (aHasPacote !== bHasPacote) {
                return aHasPacote ? 1 : -1;
            }

            // 3. pcb_descricao
            return (a.produto_pacote_barra?.pcb_descricao ?? '').localeCompare(
                b.produto_pacote_barra?.pcb_descricao ?? '',
                undefined,
                { sensitivity: 'base' },
            );
        })
        .map((item) => ({
            ...item,
            key: `${item.tap_idpk}-${item.produto_pacote_barra?.pcb_idpk || 0}`,
        }));

    return (
        <Container>
            <div className='flex flex-col gap-2'>
                <div className='flex flex-wrap gap-2'>
                    <Tag variant='outlined'>
                        Preço do produto: {numberToCurrency(pro_preco_venda)}
                    </Tag>
                    <Tag variant='outlined'>Preço mínimo: {numberToCurrency(pro_preco_minimo)}</Tag>
                </div>

                <TableDefault
                    columns={columns}
                    dataSource={dataSource}
                    loading={isFetching}
                    pagination={
                        Array.isArray(data?.registros) && data.registros.length > 10
                            ? { defaultPageSize: 10, showSizeChanger: true }
                            : false
                    }
                    rowKey='key'
                />
            </div>

            <ProdutoTabTabelaPrecoDrawer dialog={dialog} produtoIdpk={produtoIdpk} />
        </Container>
    );
}
